import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Palette,
  MousePointerClick,
  TrendingUp,
  Search,
  Sparkles,
  ArrowRight,
  ScanSearch,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const checks = [
  {
    icon: Palette,
    label: "Analyse design",
    desc: "Identité, cohérence visuelle, premiumisation.",
  },
  {
    icon: MousePointerClick,
    label: "Analyse UX",
    desc: "Navigation, clarté, parcours utilisateur.",
  },
  {
    icon: TrendingUp,
    label: "Analyse conversion",
    desc: "CTA, offre, friction, taux de transformation.",
  },
  {
    icon: Search,
    label: "Analyse SEO",
    desc: "Visibilité Google, mots-clés, structure technique.",
  },
];

export default function Audit({ onOpenQuote }) {
  const [loading, setLoading] = useState(false);

  const handleBuyAudit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/audit`, {
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Erreur de redirection vers le paiement.");
    } catch (e) {
      toast.error("Impossible de démarrer le paiement. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="audit"
      data-testid="audit-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0A0A0A 0%, #160812 30%, #0B1030 70%, #0A0A0A 100%)",
      }}
    >
      {/* Ambient glows */}
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#E52431]/20 blur-[120px]" />
      <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-[#1F2C6E]/30 blur-[120px]" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: Pitch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 text-white"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
              <ScanSearch size={14} className="text-[#E52431]" />
              <span className="text-xs tracking-[0.2em] uppercase text-white/75">
                Audit & optimisation
              </span>
            </div>

            <h2 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              Votre site ne vous{" "}
              <span className="gradient-text font-medium">
                rapporte pas de clients
              </span>{" "}
              ?
            </h2>

            <p className="mt-8 text-white/75 text-lg font-light leading-relaxed max-w-xl">
              Nous analysons votre site actuel et vous proposons des
              améliorations concrètes pour améliorer sa performance et
              générer plus de clients.
            </p>

            {/* Checks grid */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              {checks.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    data-testid={`audit-check-${i}`}
                    className="flex items-start gap-4 p-4 rounded-2xl glass hover:bg-white/[0.08] transition-colors duration-300"
                  >
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center flex-shrink-0">
                      <Icon size={18} className="text-white" strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-outfit text-base font-medium text-white">
                        {c.label}
                      </div>
                      <div className="text-xs text-white/55 font-light mt-0.5">
                        {c.desc}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right: Pricing card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="audit-card"
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl p-8 md:p-10 bg-gradient-to-b from-[#141418] to-[#0A0A0A] border border-white/10 glow-red overflow-hidden">
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#E52431] to-transparent" />

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] text-white font-semibold">
                <Sparkles size={11} /> Starter
              </div>

              <h3 className="mt-6 font-outfit text-2xl md:text-3xl font-medium text-white">
                Audit complet
              </h3>
              <p className="mt-2 text-white/55 text-sm font-light">
                Un rapport d&rsquo;expert, des actions concrètes.
              </p>

              <div className="mt-8">
                <div className="text-xs text-white/50 uppercase tracking-wider">
                  À partir de
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-outfit text-6xl md:text-7xl font-light tracking-tight gradient-text">
                    190€
                  </span>
                  <span className="text-xs font-semibold tracking-[0.15em] text-white/55">
                    TTC
                  </span>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-[#E52431]/40 bg-gradient-to-r from-[#E52431]/15 to-[#1F2C6E]/15 px-5 py-4 flex items-start gap-3">
                <Sparkles
                  size={18}
                  className="text-[#E52431] flex-shrink-0 mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-white">
                    Offert si refonte
                  </div>
                  <div className="text-xs text-white/65 font-light mt-0.5">
                    L&rsquo;audit est 100% remboursé si vous confiez la
                    refonte à EM Group.
                  </div>
                </div>
              </div>

              <button
                data-testid="audit-cta"
                onClick={handleBuyAudit}
                disabled={loading}
                className="mt-8 w-full rounded-full gradient-bg text-white font-semibold py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_40px_rgba(229,36,49,0.5)] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Redirection..." : "Commander mon audit"}
                {!loading && <ArrowRight size={16} />}
              </button>

              <Link
                to="/diagnostic-digital-gratuit"
                data-testid="audit-diagnostic-cta"
                className="mt-3 w-full rounded-full bg-white/5 border border-white/15 text-white font-medium py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-white/10 hover:border-[#E52431]/50 transition-all duration-300 inline-flex items-center justify-center gap-2"
              >
                <Sparkles size={14} className="text-[#E52431]" />
                Faire mon diagnostic gratuit
              </Link>

              <p className="mt-3 text-center text-xs text-white/50 font-light">
                ⚡ Paiement sécurisé Stripe · Rapport livré en 3–5 jours
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
